import { IDistributionWaterfallCashFlow, IPeriod, IProFormaGeneralInfo } from 'views/ProFormaTable/types';
import { formatCashFlowPeriods } from 'views/ProFormaTable/helpers';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { ProFormaGenericColumn } from '../../ProFormaGenericTable/types';

interface FieldMapping {
    gpField: string;
    lpField: string;
    totalField: string;
}

export const getAmountPeriods = <T extends Record<string, any>>(
    proForma: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence: Record<Periods, string[]>,
    periodCadence: Periods,
    items: T[],
    fieldMapping: FieldMapping = {
        gpField: 'gpAmount',
        lpField: 'lpAmount',
        totalField: 'totalAmount',
    },
): IPeriod[] => {
    if (!(proForma.startDate && proForma.saleDate)) {
        return [{
            periodName: 'MON YEAR',
            periodDate: '',
            values: {} as Record<string, string>,
        }];
    }

    const periods = formatCashFlowPeriods(cashFlowPeriodsByCadence, periodCadence, false);
    return periods?.map((periodInfo, cashFlowPeriodIndex) => {
        const values = items?.reduce((acc, item, index) => {
            if (index === cashFlowPeriodIndex) {
                return {
                    ...acc,
                    [`gp_${index}`]: item?.[fieldMapping.gpField],
                    [`lp_${index}`]: item?.[fieldMapping.lpField],
                    [`total_${index}`]: item?.[fieldMapping.totalField],
                };
            }
            return acc;
        }, {} as Record<string, string>);

        return {
            periodName: periodInfo.label,
            periodDate: periodInfo.value,
            values,
        };
    }) || [];
};

export const getDistributionPeriods = (
    proForma: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence: Record<Periods, string[]>,
    periodCadence: Periods,
    items: IDistributionWaterfallCashFlow[],
): IPeriod[] => {
    if (!(proForma.startDate && proForma.saleDate)) {
        return [{
            periodName: 'MON YEAR',
            periodDate: '',
            values: {} as Record<string, string | null>,
        }];
    }

    const periods = formatCashFlowPeriods(cashFlowPeriodsByCadence, periodCadence, false);
    return periods?.map((periodInfo, cashFlowPeriodIndex) => {
        const values = items?.reduce((acc, item, index) => {
            if (index === cashFlowPeriodIndex) {
                return {
                    ...acc,
                    [`pref_${index}`]: item?.prefReturnAmount ?? null,
                    [`tier2_${index}`]: item?.tier2Amount ?? null,
                    [`tier3_${index}`]: item?.tier3Amount ?? null,
                    [`tier4_${index}`]: item?.tier4Amount ?? null,
                } as Record<string, string | null>;
            }
            return acc;
        }, {} as Record<string, string | null>);

        return {
            periodName: periodInfo.label,
            periodDate: periodInfo.value,
            values,
        };
    }) || [];
};

export const createWaterfallColumns = (
    proForma: IProFormaGeneralInfo,
    cashFlowPeriodsByCadence: Record<Periods, string[]>,
    periodCadence: Periods,
): ProFormaGenericColumn[] => {
    if (!(proForma.startDate && proForma.saleDate)) {
        return [];
    }

    const periods = formatCashFlowPeriods(cashFlowPeriodsByCadence, periodCadence, false);
    return periods?.map((periodInfo, index) => ({
        key: `column${index + 1}`,
        field: `column${index + 1}`,
        header: periodInfo.label,
        format: 'currency',
        isFixed: false,
    })) || [];
};
